import { useMemo } from 'react';

import {
    DirectionEnum,
    FlexBox,
    FlexItem,
    Justify,
    Price,
    PriceLevel,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    View,
} from '@bytel/trilogy-react';

import { FaiType } from '@app-types/plan';

import { ByouIcon, ByouIconVariant } from '@components/byou';
import { FaiDiscountCallout } from '@components/fai-discount-callout';
import { InlinePrice } from '@components/inline-price';

import { getFaiDisplayedPricing } from '@helpers/fai';
import { capitalize } from '@helpers/wording';

import { PlanTypeId } from '@constants/plan';

import { ComparatorCell } from '@comparator/components';

interface FaiComparatorHeadProps {
    product: FaiType;
}

export function FaiComparatorHead({ product }: Readonly<FaiComparatorHeadProps>) {
    const { price, appliedPromo, priceForever, bigPromo, isByou } = useMemo(() => {
        return {
            ...getFaiDisplayedPricing(product),
            isByou: product.categories.includes(PlanTypeId.FAI_PUREBYOU),
        };
    }, [product]);

    return (
        <ComparatorCell className='has-text-centered'>
            <FlexBox direction={DirectionEnum.COLUMN}>
                {isByou ? (
                    <div style={{ minHeight: '35px' }}>
                        <div className='is-inline-flex has-custom-byou-background'>
                            <ByouIcon height={15} variant={ByouIconVariant.PRIMARY} />
                        </div>
                    </div>
                ) : (
                    <div style={{ minHeight: '35px' }} />
                )}
                <Title level={TitleLevels.FOUR} className='box-title-container-desktop' marginless>
                    {product.name}
                </Title>
                <img
                    alt={`Image de l'offre ${product.name}`}
                    aria-hidden='true'
                    className='roc-image'
                    src={product.image}
                    style={{
                        height: '168px',
                        maxWidth: 'unset',
                    }}
                />
                {price > 0 && (
                    <View flexable justify={Justify.CENTER}>
                        <Price
                            level={PriceLevel.FOUR}
                            amount={price}
                            oldAmount={price < priceForever ? priceForever : undefined}
                            period='mois'
                        />
                    </View>
                )}
                <FlexItem className='promo-wording'>
                    {appliedPromo && price !== priceForever && (
                        <Text level={TextLevels.TWO} marginless>
                            Pendant {appliedPromo.duration} mois puis{' '}
                            <InlinePrice amount={priceForever} period='mois' typo={[]} level={TextLevels.TWO} />
                        </Text>
                    )}
                    {product.obligationLabel && (
                        <Text level={TextLevels.TWO} marginless>
                            {capitalize(product.obligationLabel)}
                        </Text>
                    )}
                </FlexItem>
                {bigPromo && (
                    <FaiDiscountCallout discountAmount={bigPromo.discount} discountDuration={appliedPromo?.duration} />
                )}
            </FlexBox>
        </ComparatorCell>
    );
}
